import cx from "classnames";
import { RedesignTextArea, SCREEN_WIDTH_ENUM } from "component-library";
import { useTranslation } from "i18n";
import { useDynamicWindowWidthFor } from "shared-hooks";
import { Theme } from "shared-types";
import { useProfileTheme } from "pages/Profile/hooks/useProfileTheme";
import ExpandableSanitizedHtml from "pages/Profile/components/common/ExpandableSanitizedHtml";
import EditButton from "pages/Profile/components/common/EditButton";
import EditSectionActions from "pages/Profile/components/ProfileHeader/components/EditSectionActions";
import { HIGHLIGHTS_SHOW_MORE_CHAR_THRESHOLD_BY_BREAKPOINT } from "pages/Profile/components/ProfileHeader/constants";
import { dataTestIds } from "data-testids";

interface IExpandableTextAreaProps {
  content: string;
  hasContent: boolean;
  editExpandableTextArea: boolean;
  draftContent: string;
  editExpandableTextAreaError: string | null;
  isSaveButtonDisabled: boolean;
  isPublicView: boolean;
  alignWithVideoColumn?: boolean;
  isEditButtonVisible: boolean;
  maxLength: number;
  warningLength: number;
  placeholder: string;
  emptyFieldText: string;
  isEmptyFieldCentered?: boolean;
  emptyFieldClassName: string;
  attentionLength?: number;
  isEdit?: boolean;
  lineClamp: number;
  textareaClassName?: string;
  onOpenEdit: () => void;
  onCloseEdit: () => void;
  onSave: () => void;
  onDraftChange: (value: string) => void;
}

const ExpandableTextArea = ({
  content,
  hasContent,
  editExpandableTextArea,
  draftContent,
  editExpandableTextAreaError,
  isSaveButtonDisabled,
  isPublicView,
  alignWithVideoColumn,
  isEditButtonVisible,
  maxLength,
  warningLength,
  placeholder,
  emptyFieldText,
  isEmptyFieldCentered,
  emptyFieldClassName,
  attentionLength,
  isEdit,
  lineClamp,
  textareaClassName,
  onOpenEdit,
  onCloseEdit,
  onSave,
  onDraftChange,
}: IExpandableTextAreaProps) => {
  const { t } = useTranslation();
  const [theme] = useProfileTheme();
  const isDark = theme === Theme.Dark;
  const isSm = useDynamicWindowWidthFor(SCREEN_WIDTH_ENUM.SM);
  const isMd = useDynamicWindowWidthFor(SCREEN_WIDTH_ENUM.MD);

  const showMoreCharThreshold = isSm
    ? HIGHLIGHTS_SHOW_MORE_CHAR_THRESHOLD_BY_BREAKPOINT.sm
    : isMd
      ? HIGHLIGHTS_SHOW_MORE_CHAR_THRESHOLD_BY_BREAKPOINT.md
      : HIGHLIGHTS_SHOW_MORE_CHAR_THRESHOLD_BY_BREAKPOINT.lg;

  if (!editExpandableTextArea) {
    return (
      <div
        className={cx(
          "mt-0 flex w-full max-w-full items-center gap-3 text-sm md:flex",
          !alignWithVideoColumn && "md:max-w-[75%]",
          "isolate col-span-2 row-start-3 min-w-0 md:col-auto md:row-auto",
          isEmptyFieldCentered && "justify-center"
        )}
        data-testid={dataTestIds.profile["Profile.Header.aboutMeSection"]}
      >
        {hasContent ? (
          <div className="min-w-0 flex-1">
            <ExpandableSanitizedHtml
              html={content}
              limit={showMoreCharThreshold}
              lineClamp={lineClamp}
              contentClassName="[overflow-wrap:anywhere] text-s leading-[1.4219rem] text-grey-void dark:text-white/70"
            />
          </div>
        ) : (
          isEdit &&
          !isPublicView && (
            <span
              className={cx("text-s leading-[1.25rem]", emptyFieldClassName)}
            >
              {emptyFieldText}
            </span>
          )
        )}
        {isEditButtonVisible && isEdit && !isPublicView && (
          <EditButton
            onClick={onOpenEdit}
            className="shrink-0 text-black dark:text-white"
          />
        )}
      </div>
    );
  }

  return (
    <div
      className={cx(
        "profile-header-edit-section flex w-full max-w-full flex-col rounded-[0.625rem] p-0",
        !alignWithVideoColumn && "md:max-w-[75%]",
        "font-tertiary text-s font-normal leading-[1.4219rem] text-color-neutral-one dark:text-color-neutral-two",
        { "profile-header-edit-section--dark": isDark },
        !isDark && "text-grey-void",
        "col-span-2 row-start-3 min-w-0 md:col-auto md:row-auto"
      )}
    >
      <RedesignTextArea
        placeholder={placeholder}
        value={draftContent}
        onValueChange={onDraftChange}
        maxLength={maxLength}
        warningThreshold={warningLength}
        attentionThreshold={attentionLength}
        showCharacterCount
        alwaysShowCharacterCount
        resizable
        error={Boolean(editExpandableTextAreaError)}
        containerClassName="profile-header-edit-section__highlights-textarea"
        textareaClassName={textareaClassName}
        data-testid={dataTestIds.profile["Profile.AboutMeEdit.textArea"]}
      />
      <EditSectionActions
        onSave={onSave}
        onCancel={onCloseEdit}
        saveDisabled={isSaveButtonDisabled}
        saveLabel={t("common:button.label.save")}
        cancelLabel={t("common:button.label.cancel")}
      />
    </div>
  );
};

export default ExpandableTextArea;
